import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  Switch,
  TableHead,
} from '@mui/material';
import { useStyles } from './MagicLine.styles';
import { EmptyStateView } from '~/Components/EmptyStateView';
import YogaImg from '~/Assets/Images/SportsIllustrations/Yoga2.svg';
import { useTranslation, Trans } from 'react-i18next';
import { IIntegrationChannel, IntegrationTypes } from '~/Models/DTO/IntegrationChannels.model';
import { ILocationWithStatus } from '~/Models/interfaces/Location.model';
import { LocationStatus } from '~/Models/DTO/externalParties.model';
import SearchIcon from '@material-ui/icons/Search';
import { ToastVariant, useProvider, useToastMessage } from '~/Hooks';
import { useRequestActivateChannel } from '~/requests/adapterSvc/useRequestActivateChannel';
import { useRequestDeactivateChannel } from '~/requests/adapterSvc/useRequestDeactivateChannel';
import { ConfirmationModal } from '~/Components';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useRequestCleanupChannel } from '~/requests/adapterSvc/useRequestCleanupChannel';
import { useFetchClasses } from '~/requests/adapterSvc/useFetchChannelCategories';
import { ActionMenus } from './ActionMenus';
import { useHistory } from 'react-router-dom';
interface integrationTableProps {
  integrations: IIntegrationChannel[] | undefined;
  locations: ILocationWithStatus[] | undefined;
  integrationType: string;
}

interface deactivateChannelRequest {
  deActivateChannelId: string;
  deActivateChannelLocationName: string;
  deActivateChannelIndex: number;
}

export const ThirdPartyTable = ({
  integrations,
  locations,
  integrationType,
}: integrationTableProps) => {
  const [statusMap, setStatusMap] = useState<boolean[]>([]);
  const [tableData, setTableData] = useState<(IIntegrationChannel & ILocationWithStatus)[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [clickedIndex, setClickedIndex] = useState<number>(0);

  const [
    deactivateChannelRequest,
    setDeactivateChannelRequest,
  ] = useState<deactivateChannelRequest>({
    deActivateChannelId: '',
    deActivateChannelIndex: 0,
    deActivateChannelLocationName: '',
  });
  const [deActivateRequested, setDeActivateRequested] = useState<boolean>(false);
  const [cleanupRequested, setCleanUpRequested] = useState<string>('');
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { currentProvider } = useProvider();
  const providerDomain = currentProvider?.domain as string;
  const { showToast } = useToastMessage();

  const {
    deActivateChannelId,
    deActivateChannelIndex,
    deActivateChannelLocationName,
  } = deactivateChannelRequest;

  const {
    error: activateChannelError,
    isLoading: activateChannelLoading,
    isSuccess: channelActivated,
    mutate: activateChannel,
  } = useRequestActivateChannel();

  const {
    error: cleanupChannelError,
    isSuccess: channelCleanedUp,
    mutate: cleanupChannel,
  } = useRequestCleanupChannel();

  const {
    error: deactivateChannelError,
    isLoading: deactivateChannelLoading,
    mutate: deactivateChannel,
  } = useRequestDeactivateChannel();

  const { isLoading: fetchClassesLoading, refetch: fetchClasses } = useFetchClasses();
  const goToIntegrationCreation = () => {
    window.location.replace(`/provider/${providerDomain}/integrations/${integrationType}/new`);
  };

  const history = useHistory();
  const goToEditIntegration = (chID: string) => {
    history.push(`/provider/${providerDomain}/integrations/${integrationType}/${chID}`);
  };

  const handleSwitch = (index: number, channelId: string, status: string, name: string) => {
    if (status === 'active' || status === 'Active') {
      setDeActivateRequested(true);
      setDeactivateChannelRequest({
        deActivateChannelId: channelId,
        deActivateChannelIndex: index,
        deActivateChannelLocationName: name,
      });
    } else {
      try {
        activateChannel(channelId);

        setTableData((prevTableData) => {
          const newTableData = [...prevTableData];
          newTableData[index] = {
            ...newTableData[index],
            status: LocationStatus.Active,
          };
          return newTableData;
        });
        setStatusMap((prevStatusMap) => {
          const newStatusMap = [...prevStatusMap];
          newStatusMap[index] = true;
          return newStatusMap;
        });
      } catch (error) {
        console.error('failed to activate channel', error);
        setStatusMap((prevStatusMap) => {
          const newStatusMap = [...prevStatusMap];
          newStatusMap[index] = false;
          return newStatusMap;
        });
      }
    }
  };

  const handleDeactivateChannel = () => {
    try {
      deactivateChannel(deActivateChannelId);
      setTableData((prevTableData) => {
        const newTableData = [...prevTableData];
        newTableData[deActivateChannelIndex] = {
          ...newTableData[deActivateChannelIndex],
          status: LocationStatus.Inactive,
        };
        return newTableData;
      });
      setStatusMap((prevStatusMap) => {
        const newStatusMap = [...prevStatusMap];
        newStatusMap[deActivateChannelIndex] = false;
        return newStatusMap;
      });
    } catch (error) {
      console.error('failed to deactivate channel', error);
      setStatusMap((prevStatusMap) => {
        const newStatusMap = [...prevStatusMap];
        newStatusMap[deActivateChannelIndex] = true;
        return newStatusMap;
      });
    }
    setDeActivateRequested(false);
  };

  const handleClickOptions = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setClickedIndex(index);
  };

  const handleCloseModal = () => {
    setDeActivateRequested(false);
    setCleanUpRequested('');
  };

  const handleEditOptions = (index: number, type: number, channelId?: string) => {
    switch (type) {
      case 1:
        goToEditIntegration(tableData[index].id);
        break;
      case 2:
        !fetchClassesLoading && fetchClasses(channelId as string, false);
        break;
      case 3:
        setCleanUpRequested(channelId as string);
        break;
      default:
        break;
    }
  };

  const handleCleanUpChannel = () => {
    cleanupChannel(cleanupRequested);
    setCleanUpRequested('');
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const isActive = (status: LocationStatus): boolean => {
    if (
      status.toLowerCase() === LocationStatus.Active.toLowerCase() ||
      status.toLowerCase() === LocationStatus.Active
    ) {
      return true;
    } else {
      return false;
    }
  };

  const mergeResults = (
    integrations: IIntegrationChannel[],
    locations: ILocationWithStatus[] | undefined,
  ): (IIntegrationChannel & ILocationWithStatus)[] => {
    const filteredIntegrations = integrations.filter((obj) => obj.integration === integrationType);
    const mergedArray: (IIntegrationChannel & ILocationWithStatus)[] = [];

    filteredIntegrations.forEach((obj1) => {
      const matchingObj2 = locations?.find((obj2) => obj1.location_id === obj2.uuid);
      if (matchingObj2) {
        const mergedObj: IIntegrationChannel & ILocationWithStatus = {
          ...obj1,
          ...matchingObj2,
          id: obj1.id,
          status: obj1.status as LocationStatus,
        };
        mergedArray.push(mergedObj);
      }
    });

    return mergedArray;
  };

  const sortData = (data: (IIntegrationChannel & ILocationWithStatus)[]) => {
    const sortedData = [...data];
    sortedData.sort((a, b) => {
      const statusCompare = a.status.localeCompare(b.status);
      if (statusCompare === 0) {
        return a.name.localeCompare(b.name);
      }
      return statusCompare;
    });
    return sortedData;
  };

  useEffect(() => {
    if (integrations && locations) {
      const result = mergeResults(integrations, locations);
      const sortedResult = sortData(result);
      setTableData(sortedResult);
      const newStatusMap = sortedResult.map((item) => isActive(item.status));
      setStatusMap(newStatusMap);
    }
  }, [integrations, locations]);

  useEffect(() => {
    if (activateChannelError) {
      showToast('Magicline', t('magicline.activate.channel.error'), ToastVariant.ERROR, true);
    }
    if (deactivateChannelError) {
      showToast('Magicline', t('magicline.deactivate.channel.error'), ToastVariant.ERROR, true);
    }
    if (channelActivated) {
      showToast('Magicline', t('magicline.activate.channel.success'), ToastVariant.SUCCESS, true);
    }
    if (channelCleanedUp) {
      showToast('Magicline', t('magicline.cleanup.channel.success'), ToastVariant.SUCCESS, true);
    }
    if (cleanupChannelError) {
      showToast('Magicline', t('magicline.cleanup.channel.error'), ToastVariant.ERROR, true);
    }
  }, [
    activateChannelError,
    deactivateChannelError,
    channelActivated,
    cleanupChannelError,
    channelCleanedUp,
  ]);

  const determineTitle = () => {
    switch (integrationType) {
      case IntegrationTypes.Magicline:
        return t('integrations.magicline.add');
      case IntegrationTypes.Eversports:
        return t('integrations.eversports.add');
      case IntegrationTypes.Virtuagym:
        return t('integrations.virtuagym.add');
      case IntegrationTypes.Zingfit:
        return t('integrations.zingfit.add');
      case IntegrationTypes.Mindbody:
        return t('integrations.mindbody.add');
      case IntegrationTypes.VirtuagymV1:
        return t('integrations.virtuagymV1.add');
      case IntegrationTypes.Resamania:
        return t('integrations.resamania.add');
      default:
        return t('integrations.magicline.add');
    }
  };
  const determineDescription = () => {
    switch (integrationType) {
      case IntegrationTypes.Magicline:
        return t('integrations.magicline.add.description');
      case IntegrationTypes.Eversports:
        return t('integrations.eversports.add.description');
      case IntegrationTypes.Virtuagym:
        return t('integrations.virtuagym.add.description');
      case IntegrationTypes.Zingfit:
        return t('integrations.zingfit.add.description');
      case IntegrationTypes.Mindbody:
        return t('integrations.mindbody.add.description');
      case IntegrationTypes.VirtuagymV1:
        return t('integrations.virtuagymV1.add.description');
      case IntegrationTypes.Resamania:
        return t('integrations.resamania.add.description');
      default:
        return t('integrations.magicline.add.description');
    }
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      {deActivateRequested ? (
        <ConfirmationModal
          open
          title={`${t('magicline.disable.heading')} ${deActivateChannelLocationName}?`}
          cancel={{ action: handleCloseModal }}
          confirm={{ action: handleDeactivateChannel, title: t('app.confirmationModal.disable') }}
          destructive
        >
          <Typography className={classes.modalParagraph} variant="body1">
            <Trans
              i18nKey="magicline.disable.message"
              values={{ location: deActivateChannelLocationName }}
              components={{ b: <b /> }}
            />
          </Typography>
          <Typography className={classes.confirmationParagraph} variant="body1">
            <WarningAmberIcon />
            {t('app.confirmation.message')}
          </Typography>
        </ConfirmationModal>
      ) : cleanupRequested.length !== 0 ? (
        <ConfirmationModal
          open
          title={`${t('app.cleanup')}?`}
          cancel={{ action: handleCloseModal }}
          confirm={{ action: handleCleanUpChannel, title: t('app.confirmationModal.cleanup') }}
          destructive
        >
          <Typography className={classes.modalParagraph} variant="body1">
            <Trans i18nKey="magicline.cleanup.message" components={{ b: <b /> }} />
          </Typography>
          <Typography className={classes.confirmationParagraph} variant="body1">
            <WarningAmberIcon />
            {t('app.confirmation.message')}
          </Typography>
        </ConfirmationModal>
      ) : (
        ''
      )}
      <div className={classes.searchBarWrapper}>
        <div className={classes.searchBar}>
          <SearchIcon color="inherit" fontSize="inherit" />
          <input
            className={classes.searchInput}
            placeholder={t('search.by.location.name')}
            value={searchTerm}
            onChange={handleInputChange}
          />
        </div>
      </div>
      <Table>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell className={`${classes.tableCell} ${classes.tableCellProperties}`}>
              {t('form.field.name')}
            </TableCell>
            <TableCell className={`${classes.tableCell} ${classes.tableCellProperties}`}>
              {t('form.field.location.address')}
            </TableCell>
            <TableCell className={`${classes.tableCell} ${classes.tableCellProperties}`}>
              {t('app.import.table.status')}
            </TableCell>
            <TableCell className={`${classes.tableCell} ${classes.tableCellProperties}`} />
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData?.map((data, index) => (
            <TableRow key={data.id}>
              <TableCell width={'35%'} className={`${classes.tableCellProperties}`}>
                <div className={`${classes.tableCellName} `}>
                  <Typography
                    variant="body1"
                    className={classes.name}
                    onClick={() => goToEditIntegration(data.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {data.name}
                  </Typography>
                  <Typography
                    variant="body1"
                    className={`${classes.addressCellResponsive} ${classes.address}`}
                  >
                    {data.street}, {data.city}, {data.zipCode}
                  </Typography>
                </div>
              </TableCell>
              <TableCell
                width={'40%'}
                className={`${classes.tableCellProperties} ${classes.tableCellAddress}`}
              >
                <Typography
                  variant="body1"
                  className={classes.address}
                  onClick={() => goToEditIntegration(data.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {data.street}, {data.city}, {data.zipCode}
                </Typography>
              </TableCell>
              <TableCell width={'20%'} className={`${classes.tableCellProperties}`}>
                <div className={`${classes.tableCellStatus}`}>
                  {(!activateChannelLoading || !deactivateChannelLoading) && (
                    <Switch
                      data-testid="magicline-switch"
                      checked={statusMap[index] || false}
                      onChange={() => handleSwitch(index, data.id, data.status, data.name)}
                      classes={{
                        root: classes.switchRoot,
                        switchBase: classes.switchBase,
                        thumb: classes.thumb,
                        track: classes.track,
                        checked: classes.checked,
                      }}
                    />
                  )}
                </div>
              </TableCell>
              <TableCell className={`${classes.tableCellProperties}`} width={'5%'}>
                <ActionMenus
                  handleClickOptions={handleClickOptions}
                  index={index}
                  clickedIndex={clickedIndex}
                  handleEditOptions={handleEditOptions}
                  className={classes.actionMenu}
                  channelId={data.id}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {(!tableData || tableData?.length === 0) && (
        <EmptyStateView
          imgSrc={YogaImg}
          imgAlt={determineTitle()}
          title={determineTitle()}
          description={determineDescription()}
          action={{ name: t('app.add.form') }}
          onAction={goToIntegrationCreation}
        />
      )}
    </TableContainer>
  );
};
